import { SyntheticEvent, useContext, useEffect, useState } from "react";
import { DataRow } from "./DataRow";
import { useCardSwapSearchResults } from "./useCardSwapSearchResults";
import { CardSwapSearchResultDto } from "../dtos/CardSwapSearchResultDto";
import { GridCellParams, GridSortItem, MuiEvent } from "@mui/x-data-grid";
import { CardSwapStatusCode, cardSwapStatusCodes } from "../enums/CardSwapStatusCode";
import { DialogResult } from "../enums/DialogResult";
import { CardSwapUpdateDto } from "../dtos/CardSwapUpdateDto";
import VspPortalContext from "../../../store/VspPortalContext";
import { UserGroup } from "../../../enums/UserGroup";
import { CardSwapSearchDto } from "../dtos/CardSwapSearchDto";
import { VspPortalActions } from "../../../store/VspPortalActions";
import { sleep } from "../../../utils";


export const useCardSwapSearchResultsHandlers = ( cardSwapStatuses : string[ ], villageCodes : string[ ] ) => {

    const [ auditDialogOpen, setAuditDialogOpen ] = useState< boolean >( false );
    const [ auditCardSwapId, setAuditCardSwapId ] = useState< number >( );
    const [ cardCollectNotificationFailedDialogOpen, setCardCollectNotificationFailedDialogOpen ] = useState< boolean >( false );
    const [ cardSwapFinalDialogOpen, setCardSwapFinalDialogOpen ] = useState< boolean >( false );
    const [ cardSwapSearchDto, setCardSwapSearchDto ] = useState< CardSwapSearchDto >( );
    const [ cardSwapToggleDialogOpen, setCardSwapToggleDialogOpen ] = useState< boolean >( false );
    const [ creatingNewCardDialogOpen, setCreatingNewCardDialogOpen ] = useState< boolean >( false );
    const [ inlineEditingRowId, setInlineEditingRowId ] = useState< number >( -1 );
    const [ isLoading, setIsLoading ] = useState< boolean >( true );
    const [ isSendingNotification, setIsSendingNotification ] = useState< boolean >( false );
    const [ newCardCreatedDialogOpen, setNewCardCreatedDialogOpen ] = useState< boolean >( false );
    const [ rows, setRows ] = useState< DataRow[ ] >( [ ] );
    const [ selectedCardSwapSearchResult, setSelectedCardSwapSearchResult ] = useState< CardSwapSearchResultDto >(  );
    const [ sortModel, setSortModel ] = useState< GridSortItem[ ] > ( [ { field: 'surname', sort: 'asc' } ] );

    const { 
        exportCardSwapSearchResults
        , fetchData
        , sendNotification
    } = useCardSwapSearchResults( cardSwapStatuses, villageCodes );

    const { dispatch, state } = useContext( VspPortalContext );


    useEffect( ( ) => {

        setSortModel( [ state.cardSwapSearchResultSorting ] );

    }, [ ] );
    
    
    const handleAuditButtonClick = ( event : SyntheticEvent, row : DataRow ) => {

        event.stopPropagation( );
        setAuditCardSwapId( row.id );
        setAuditDialogOpen( true );

    };


    const handleAuditDialogClose = ( ) => setAuditDialogOpen( false );


    const handleCancelInlineEditing = async( isUpdated : boolean ) => { 
        
        setInlineEditingRowId( -1 ); 

        if( isUpdated ){
            
            await handleFetchData( );

        }

    };
        

    const handleCreatingNewCardDialogClose = async( isUpdated : boolean ) => { 
        
        setSelectedCardSwapSearchResult( undefined );
        setCreatingNewCardDialogOpen( false );

        if( isUpdated ){
            
            await handleFetchData( );

        }

    }


    const handleCardCollectNotificationFailedDialogClose = async( isUpdated : boolean ) => { 
        
        setCardCollectNotificationFailedDialogOpen( false );

        if( isUpdated ){
            
            await handleFetchData( );
            setCardSwapToggleDialogOpen( true );

        } else {
            
            setSelectedCardSwapSearchResult( undefined );
            
        }

    }


    const handleCardSwapFinalDialogClose = async( isUpdated : boolean ) => {

        setSelectedCardSwapSearchResult( undefined );
        setCardSwapFinalDialogOpen( false );
        
        if( isUpdated ){
            
            await handleFetchData( );

        }

    };


    const handleCardSwapToggleDialogClose = async( isUpdated : boolean ) => { 
        
        setSelectedCardSwapSearchResult( undefined );
        setCardSwapToggleDialogOpen( false );

        if( isUpdated ){
            
            await handleFetchData( );

        }

    }


    const handleCellClick = ( params: GridCellParams, _: MuiEvent<React.MouseEvent> ) => {

        if( params.field === 'newCardNumber' && params.row.cardSwapStatus === cardSwapStatusCodes[ CardSwapStatusCode.CreatingNewCard ] ){
            
            setInlineEditingRowId( params.row.id );

        } else {
            
            handleSelectRow( params.row.id );

        }

    }


    const handleExportClick = async ( ) => { 
        
        setIsLoading( true );

        let pageNumber = 0;
        const pageSize = 1000;
        const sorting: GridSortItem = { field: 'surname', sort: 'asc' };;
        let exportRows: DataRow[] = [];
        
        let result = await fetchData( pageNumber, pageSize, sorting );
        const totalCount = result.cardSwapSearchResults?.totalCount ?? 0;

        exportRows = [...result.rows];
        pageNumber++;

        while( pageNumber * pageSize <= totalCount ){

            result = await fetchData( pageNumber, pageSize, sorting );
            exportRows = [...exportRows, ...result.rows];
            pageNumber++;

        }

        exportCardSwapSearchResults( exportRows );
        setIsLoading( false );
    
    };


    const handleFetchData = async ( ) => {

        setIsLoading( true );
        const pageNumber = state.cardSwapSearchResultPageNumber;
        const pageSize = state.cardSwapSearchResultPageSize;
        const sorting = state.cardSwapSearchResultSorting;

        const { cardSwapSearchResults, rows } = await fetchData( pageNumber, pageSize, sorting );

        setCardSwapSearchDto( cardSwapSearchResults as CardSwapSearchDto );
        setRows( rows );
        setIsLoading( false );

    };


    const handleNewCardCreatedDialogClose = async( dialogResult : DialogResult ) => { 
        
        setNewCardCreatedDialogOpen( false );

        if( dialogResult === DialogResult.Update || dialogResult === DialogResult.UpdateWithRedirect ){
            
            await handleFetchData( );

            if( dialogResult === DialogResult.UpdateWithRedirect ){

                const cardSwapSearchResult = { ...selectedCardSwapSearchResult, refCardSwapStatusCode : CardSwapStatusCode.ReadyToCollect };
                setSelectedCardSwapSearchResult( cardSwapSearchResult as CardSwapSearchResultDto );
                setCardSwapToggleDialogOpen( true );
                
            }

        } else {
            
            setSelectedCardSwapSearchResult( undefined );

        }

    }


    const handleNotifyForCollectionClick = async ( event : SyntheticEvent, row : DataRow ) => {

        event.stopPropagation( );

        const cardSwapUpdateDto : CardSwapUpdateDto = {

            cardSwapStatusCode : CardSwapStatusCode.ReadyToCollect,
            id : row.id,
            updatedBy : state.username

        };

        setIsSendingNotification( true );
        await sendNotification( cardSwapUpdateDto );
        setIsSendingNotification( false );
        await handleFetchData( );

    };


    const handleSelectRow = ( rowId : number ) => {

        const cardSwapSearchResult = cardSwapSearchDto?.items.find( r => r.id === rowId );

        if( !cardSwapSearchResult ){
            
            return;

        }

        setSelectedCardSwapSearchResult( cardSwapSearchResult );

        if( cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.CardCollectNotificationFailed ) {


            setCardCollectNotificationFailedDialogOpen( true );


        } else if( cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.CreatingNewCard ) {

            if( state.userGroups.includes( UserGroup.WaioSecurity ) || state.userGroups.includes( UserGroup.WaioReception ) ) {

                setCreatingNewCardDialogOpen( true );

            }

        } else if( cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.DeactivatingOldCards 
                   ||  cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.ReadyToCollect
                   || cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.ReturningOldCards ) {

            setCardSwapToggleDialogOpen( true );

        } else if( cardSwapSearchResult?.refCardSwapStatusCode === CardSwapStatusCode.NewCardCreated ) {

            if( state.userGroups.includes( UserGroup.WaioSecurity ) || state.userGroups.includes( UserGroup.WaioReception ) ) {

                setNewCardCreatedDialogOpen( true );

            }

        } else {

            setCardSwapFinalDialogOpen( true );

        }

    };


    const handleSortModelChange = async( newSortModel: GridSortItem[ ] ) => {

        dispatch({ type: VspPortalActions.SetCardSwapSearchResultSorting, payload: newSortModel[0] });
        await sleep( 1500 );
        setSortModel( newSortModel );
        
    };


    return { 
        auditDialogOpen
        , auditCardSwapId
        , cardCollectNotificationFailedDialogOpen
        , cardSwapFinalDialogOpen
        , cardSwapSearchDto
        , cardSwapToggleDialogOpen
        , creatingNewCardDialogOpen
        , handleAuditButtonClick
        , handleAuditDialogClose
        , handleCancelInlineEditing
        , handleCreatingNewCardDialogClose
        , handleCardCollectNotificationFailedDialogClose
        , handleCardSwapFinalDialogClose
        , handleCardSwapToggleDialogClose
        , handleCellClick
        , handleExportClick
        , handleFetchData
        , handleNewCardCreatedDialogClose
        , handleNotifyForCollectionClick
        , handleSortModelChange
        , inlineEditingRowId
        , isLoading
        , isSendingNotification
        , newCardCreatedDialogOpen
        , rows
        , selectedCardSwapSearchResult
        , sortModel
    };

};

